import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';
import type { ApplicantDetailArea } from './applicant-notes.service';

export type ApplicantDocumentCategory =
  | 'Passport'
  | 'Transcript'
  | 'IELTS'
  | 'Visa'
  | 'Financial'
  | 'Other';

export const APPLICANT_DOCUMENT_CATEGORIES: ApplicantDocumentCategory[] = [
  'Passport',
  'Transcript',
  'IELTS',
  'Visa',
  'Financial',
  'Other',
];

export type ApplicantDocumentDto = {
  id: string;
  applicantId: string;
  documentName: string;
  category?: ApplicantDocumentCategory;
  originalFileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  mediaAssetId: string;
  uploadedBy: string;
  uploadedByDisplayName?: string;
  uploadedAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type UploadApplicantDocumentItem = {
  documentName: string;
  category?: ApplicantDocumentCategory;
};

export type ApplicantDocumentAccessIntent = 'view' | 'download';

@Injectable({ providedIn: 'root' })
export class ApplicantDocumentsService {
  private readonly http = inject(HttpClient);

  constructor(private readonly api: ApiService) {}

  list(area: ApplicantDetailArea, applicantId: string) {
    const prefix = area === 'tenant' ? 'tenant/applicants' : 'platform/applicants';
    return this.api
      .get<ApiEnvelope<ApplicantDocumentDto[]>>(`${prefix}/${applicantId}/documents`)
      .pipe(map((r) => r.data));
  }

  upload(area: ApplicantDetailArea, applicantId: string, files: File[], items: UploadApplicantDocumentItem[]) {
    const prefix = area === 'tenant' ? 'tenant/applicants' : 'platform/applicants';
    const form = new FormData();
    files.forEach((file) => form.append('files', file));
    form.append('metadata', JSON.stringify({ items }));
    return this.http
      .post<ApiEnvelope<ApplicantDocumentDto[]>>(
        `${this.api.baseUrl}/${prefix}/${applicantId}/documents`,
        form,
        { withCredentials: true },
      )
      .pipe(map((r) => r.data));
  }

  access(
    area: ApplicantDetailArea,
    applicantId: string,
    documentId: string,
    intent: ApplicantDocumentAccessIntent,
  ) {
    const prefix = area === 'tenant' ? 'tenant/applicants' : 'platform/applicants';
    return this.api
      .post<ApiEnvelope<{ url: string }>>(`${prefix}/${applicantId}/documents/${documentId}/access`, {
        intent,
      })
      .pipe(map((r) => r.data));
  }

  delete(area: ApplicantDetailArea, applicantId: string, documentId: string) {
    const prefix = area === 'tenant' ? 'tenant/applicants' : 'platform/applicants';
    return this.api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`${prefix}/${applicantId}/documents/${documentId}`)
      .pipe(map((r) => r.data));
  }
}
